import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { CoreConfig } from '../../../CoreConfig';
import { Configuration } from '../../../Domain/DTOs/Configuration/ConfigurationDTO';
import { component } from '../../Decorators/Component';
import { DatePickerButtonView } from './DatePickerButtonView';
import { ParagraphView } from '../Typography/Paragraph';
const daysOfWeek = ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'].map(x => x.toLowerCase());
const date_format = 'YYYY-MM-DD';
function prepareCell(date, dayNumber, isCurrentMonth, options) {
    var _a;
    const value = date.clone();
    value.setDate(dayNumber);
    value.setHours(0, 0, 0, 0);
    let disabled = false;
    if (options === null || options === void 0 ? void 0 : options.minDate) {
        const minDate = options.minDate.clone();
        minDate.setHours(0, 0, 0, 0);
        if (value.getUnix() < minDate.getUnix()) {
            disabled = true;
        }
    }
    if (options === null || options === void 0 ? void 0 : options.maxDate) {
        const maxDate = options.maxDate.clone();
        maxDate.setHours(0, 0, 0, 0);
        if (value.getUnix() > maxDate.getUnix()) {
            disabled = true;
        }
    }
    if ((_a = options === null || options === void 0 ? void 0 : options.disabledDates) === null || _a === void 0 ? void 0 : _a.includes(value.format(date_format))) {
        disabled = true;
    }
    return {
        text: dayNumber.toString(),
        isCurrentMonth,
        disabled,
        date: value.format(date_format),
        value
    };
}
function getCellsFromPrevMonth(date, options) {
    const calendarCells = [];
    const clone = date.clone();
    clone.setDate(1);
    const firstDay = clone.format('dd').toLowerCase();
    const firstDayOfWeek = daysOfWeek.findIndex(x => x === firstDay);
    clone.removeMonths(1);
    for (let i = 0; i < firstDayOfWeek; i++) {
        calendarCells.unshift(prepareCell(clone, clone.getDaysInMonth() - i, false, options));
    }
    return calendarCells;
}
function getCellsFromNextMonth(date, options) {
    const daysInMonth = date.getDaysInMonth();
    const calendarCells = [];
    const lastDayClone = date.clone();
    lastDayClone.setDate(daysInMonth);
    const lastDay = lastDayClone.format('dd').toLowerCase();
    const lastDayOfWeek = 6 - daysOfWeek.findIndex(x => x === lastDay);
    lastDayClone.setDate(1);
    lastDayClone.addMonths(1);
    for (let i = 0; i < lastDayOfWeek; i++) {
        calendarCells.push(prepareCell(lastDayClone, i + 1, false, options));
    }
    return calendarCells;
}
function getCalendarCells(date, options) {
    const daysInMonth = date.getDaysInMonth();
    const calendarCells = [];
    for (let i = 0; i < daysInMonth; i++) {
        calendarCells.push(prepareCell(date, i + 1, true, options));
    }
    return [...getCellsFromPrevMonth(date, options), ...calendarCells, ...getCellsFromNextMonth(date, options)];
}
function getCalendarRows(date, options) {
    const cells = getCalendarCells(date, options);
    const rows = [];
    for (let i = 0; i < cells.length; i += 7) {
        rows.push(cells.slice(i, i + 7));
    }
    return rows;
}
const dir = { dir: 'ltr' };
export const DatePickerCalendarView = component({
    provider: CoreConfig.provider,
    moduleName: CoreConfig.moduleName,
    componentName: Configuration.ComponentNames.datePickerCalendar,
    style: {
        container: {
            marginHorizontal: -10
        },
        row: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center'
        },
        cell: {
            flex: 1,
            paddingHorizontal: 5,
            marginBottom: 0,
            textTransform: 'uppercase',
            textAlign: 'center',
            fontSize: 12
        }
    }
})(({ style, onChange, shownDate, selectedDate, minDate, maxDate, disabledDates }) => {
    const handleSelectDate = useCallback((value) => onChange(value), [onChange]);
    const rows = useMemo(() => getCalendarRows(shownDate, { minDate, maxDate, disabledDates }), [shownDate, minDate, maxDate, disabledDates]);
    const formatted_date = useMemo(() => selectedDate === null || selectedDate === void 0 ? void 0 : selectedDate.format(date_format), [selectedDate === null || selectedDate === void 0 ? void 0 : selectedDate.getTime()]);
    const formatted_date_today = useMemo(() => Date.create().format(date_format), []);
    return (_jsxs(View, Object.assign({ style: style === null || style === void 0 ? void 0 : style.container }, dir, { children: [_jsx(View, Object.assign({ style: style === null || style === void 0 ? void 0 : style.row }, dir, { children: rows[0].map(({ value }, i) => (_jsx(ParagraphView, { textStyle: style === null || style === void 0 ? void 0 : style.cell, children: value.format('ddd') }, i))) })), rows.map((cells, rowIndex) => (_jsx(View, Object.assign({ style: style === null || style === void 0 ? void 0 : style.row }, dir, { children: cells.map(({ text, value, date, isCurrentMonth, disabled }, i) => (_jsx(DatePickerButtonView, { value: value, text: text, border: date === formatted_date_today, onChange: () => handleSelectDate(value), inactive: !isCurrentMonth, disabled: disabled, selected: date === formatted_date }, `${text}:${i}`))) }), rowIndex)))] })));
});
